'use client'

import { useEffect } from 'react'

// Swiper marks
// off-screen slides with aria-hidden="true" but leaves the links/buttons inside
// them in the tab order — which fails the axe "aria-hidden-focus" rule and
// drops the Lighthouse Accessibility score. Mirroring that hidden state onto the
// `inert` attribute removes the off-screen subtree from focus order AND the
// accessibility tree, clearing the violation with no layout/visual change.
//
// Scoped strictly to slide elements (not every aria-hidden node) so we never
// inadvertently disable an intentionally hidden-but-interactive widget. A single
// rAF-debounced MutationObserver covers every carousel on the page, including
// ones that mount/refresh after the client-side CMS merge.
const SLIDE_SELECTOR = '.swiper-slide'

export function useInertHiddenSlides() {
  useEffect(() => {
    let raf = 0

    const sync = () => {
      raf = 0
      document.querySelectorAll(SLIDE_SELECTOR).forEach(el => {
        const hidden = el.getAttribute('aria-hidden') === 'true'
        if (hidden && !el.hasAttribute('inert')) el.setAttribute('inert', '')
        else if (!hidden && el.hasAttribute('inert')) el.removeAttribute('inert')
      })
    }

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(sync)
    }

    schedule()
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      // `inert` is intentionally excluded so our own writes don't re-trigger.
      attributeFilter: ['aria-hidden', 'class']
    })

    return () => {
      observer.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])
}
